import React from 'react';
import { Tooltip } from 'antd';

import type { LaneMode } from '../../../apis/lanePlanner';
import ModeIcon from './ModeIcon';
import styles from './styles.module.css';

interface Tile {
  key: LaneMode | 'multi';
  label: string;
}

/** The Figma's four tiles: three modes that can be ticked together, and Multi modal (a composed itinerary, not built yet). */
const TILES: Tile[] = [
  {
    key: 'air',
    label: 'Flight',
  },
  {
    key: 'ocean',
    label: 'Ocean',
  },
  {
    key: 'road',
    label: 'Road',
  },
  {
    key: 'multi',
    label: 'Multi modal',
  },
];

interface ModeTilesProps {
  modes: LaneMode[];
  /** Modes at least one of the company's vendors can quote. */
  available: ReadonlySet<LaneMode>;
  onChange: (modes: LaneMode[]) => void;
}

/** Tick one or more modes to search them together; at least one stays ticked. */
const ModeTiles: React.FC<ModeTilesProps> = ({
  modes,
  available,
  onChange,
}) => {
  const toggle = (mode: LaneMode): void => {
    if (modes.includes(mode)) {
      if (modes.length > 1) onChange(modes.filter((m) => m !== mode));
    } else {
      onChange([...modes, mode]);
    }
  };

  return (
    <div className={styles.modeGrid}>
      {TILES.map((tile) => {
        const mode = tile.key === 'multi' ? null : tile.key;
        const active = mode != null && modes.includes(mode);
        // A ticked mode stays clickable so it can always be unticked, even
        // if the company's vendors no longer quote it (a re-run saved search).
        const enabled = mode != null && (available.has(mode) || active);
        const button = (
          <button
            type='button'
            key={tile.key}
            className={[
              styles.modeTile,
              active ? styles.modeTileActive : '',
              !enabled ? styles.modeTileDisabled : '',
            ]
              .filter(Boolean)
              .join(' ')}
            disabled={!enabled && !active}
            aria-pressed={active}
            onClick={() => mode && toggle(mode)}
          >
            <span className={styles.modeTileLabel}>
              <span className={styles.modeTileIcon}>
                <ModeIcon mode={tile.key} />
              </span>
              {tile.label}
            </span>
            <span
              className={`${styles.modeBox} ${active ? styles.modeBoxOn : ''}`}
            >
              {active && (
                <svg width='10' height='10' viewBox='0 0 12 12' fill='none'>
                  <path
                    d='M2 6.5 L4.8 9 L10 3'
                    stroke='var(--white)'
                    strokeWidth='2'
                    fill='none'
                  />
                </svg>
              )}
            </span>
          </button>
        );
        return enabled ? (
          button
        ) : (
          <Tooltip
            key={tile.key}
            title={
              mode == null
                ? 'Coming soon'
                : 'None of your connected vendors quotes this mode'
            }
          >
            {button}
          </Tooltip>
        );
      })}
    </div>
  );
};

export default ModeTiles;
